import type { SocialPage } from "@nlc/shared-types";

import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Loader } from "@/components/Loader";
import { Radio, RadioGroup } from "@/components/radio-group";
import { SearchField, SearchInput } from "@/components/search-field";
import { useGetBrandsByProjectId } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";
import { useUserTimezone } from "@/hooks/useUserTimezone";

import type { Platform } from "./post-type-step/postTypes";

import { useScheduledPosts } from "../../hooks/useScheduledPosts";
import { useSocialPages } from "../../hooks/useSocialPages";
import { inTimeZone } from "../schedule/timeZone";
import { FacebookOutline, InstagramOutline } from "./PlatformOutlineIcons";
import { PLATFORM_LABELS } from "./post-type-step/postTypes";
import { SelectionCheck } from "./SelectionCheck";

const PLATFORMS: Platform[] = ["instagram", "facebook"];
const PLATFORM_ICONS = {
  facebook: FacebookOutline,
  instagram: InstagramOutline,
};

export function BrandStep({
  onGoToSettings,
  onSelectBrand,
  selectedBrandId,
}: {
  onGoToSettings: () => void;
  onSelectBrand: (brandId: number) => void;
  selectedBrandId?: number;
}) {
  const projectId = useGetCurrentProjectId();
  const { data: brands = [], isLoading: isLoadingBrands } =
    useGetBrandsByProjectId(projectId);
  const { data, isLoading: isLoadingPages } = useSocialPages();
  const { data: scheduledPostsData } = useScheduledPosts();
  const { timezone } = useUserTimezone();
  const [search, setSearch] = useState("");

  if (isLoadingBrands || isLoadingPages) return <Loader className="py-20" />;

  const pages = data?.pages ?? [];
  const now = inTimeZone(timezone);
  const postsThisWeek = (scheduledPostsData?.posts ?? []).filter(
    (post) =>
      post.status === "scheduled" &&
      inTimeZone(timezone, new Date(post.scheduledAt)).isSame(now, "week"),
  );
  const visibleBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const isSingleBrand = brands.length === 1;

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-xs">
        <h2 className="text-xl font-semibold text-text-primary">
          Which brand is this post for?
        </h2>
        <p className="text-sm text-text-tertiary">
          It will be published from the Meta account connected to the brand.
        </p>
      </div>

      {!isSingleBrand && (
        <SearchField
          aria-label="Search brands"
          onChange={setSearch}
          value={search}
        >
          <SearchInput
            onClear={() => setSearch("")}
            placeholder="Search brands"
          />
        </SearchField>
      )}

      {visibleBrands.length === 0 && (
        <p className="py-3xl text-center text-sm text-text-tertiary">
          No brands match &ldquo;{search.trim()}&rdquo;
        </p>
      )}

      <RadioGroup
        aria-label="Brand"
        className={twMerge(
          "grid auto-rows-fr grid-cols-1 gap-lg md:grid-cols-2",
          !isSingleBrand && "xl:grid-cols-3",
        )}
        onChange={(brandId) => onSelectBrand(Number(brandId))}
        value={selectedBrandId ? String(selectedBrandId) : null}
      >
        {visibleBrands.map((brand) => {
          const brandPages = pages.filter((page) => page.brand.id === brand.id);
          const isConnected = brandPages.length > 0;
          const scheduledCount = postsThisWeek.filter(
            (post) => post.brand.id === brand.id,
          ).length;

          return (
            <div
              className={twMerge(
                "flex flex-col gap-lg rounded-xl border border-border-secondary bg-bg-primary p-xl",
                isSingleBrand && "min-h-52 justify-between p-2xl",
                "has-data-selected:border-border-brand has-data-selected:ring-1 has-data-selected:ring-border-brand",
              )}
              key={brand.id}
            >
              <Radio
                className="cursor-pointer data-disabled:cursor-not-allowed data-disabled:opacity-50"
                isDisabled={!isConnected}
                render={({ isSelected }) => (
                  <div className="flex items-center gap-lg">
                    <Avatar
                      alt={brand.name}
                      className={twMerge(
                        "size-12 rounded-full",
                        isSingleBrand && "size-16",
                      )}
                      initials={brand.name.slice(0, 2).toUpperCase()}
                      src={brand.profilePicture ?? undefined}
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span
                        className={twMerge(
                          "truncate font-semibold text-text-primary",
                          isSingleBrand && "text-lg",
                        )}
                      >
                        {brand.name}
                      </span>
                      <span className="truncate text-sm text-text-tertiary">
                        {scheduledPostsLabel(scheduledCount)}
                      </span>
                    </div>
                    <Badge
                      isRounded
                      size="small"
                      variant={isConnected ? "success" : "primary"}
                    >
                      {isConnected ? "Connected" : "Not connected"}
                    </Badge>
                    <SelectionCheck isSelected={isSelected} />
                  </div>
                )}
                value={String(brand.id)}
              />
              <div className="flex flex-col gap-md border-t border-border-secondary pt-lg">
                {PLATFORMS.map((platform) => (
                  <PlatformAccount
                    key={platform}
                    page={brandPages.find((page) => page.platform === platform)}
                    platform={platform}
                  />
                ))}
              </div>
              {!isConnected && (
                <Button
                  className="self-start"
                  onPress={onGoToSettings}
                  size="sm"
                  variant="tertiary"
                >
                  Connect in Settings
                </Button>
              )}
            </div>
          );
        })}
        {isSingleBrand && visibleBrands.length > 0 && <AddBrandCard />}
      </RadioGroup>
    </div>
  );
}

function AddBrandCard() {
  return (
    <Link
      className="flex flex-col items-center justify-center gap-lg rounded-xl border border-dashed border-border-primary bg-bg-primary p-2xl text-center outline-hidden hover:bg-bg-secondary focus-visible:ring-2 focus-visible:ring-border-brand"
      to="/brands/create"
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-bg-secondary text-fg-secondary">
        <Plus className="size-6" />
      </span>
      <div className="flex flex-col gap-xs">
        <span className="font-semibold text-text-primary">
          Add another brand
        </span>
        <span className="text-sm text-text-tertiary">
          Connect more Instagram and Facebook accounts to post for them too
        </span>
      </div>
    </Link>
  );
}

function PlatformAccount({
  page,
  platform,
}: {
  page?: SocialPage;
  platform: Platform;
}) {
  const PlatformIcon = PLATFORM_ICONS[platform];

  return (
    <div className="flex min-w-0 items-center gap-sm text-sm">
      <PlatformIcon
        className={twMerge(
          "shrink-0",
          page ? "text-fg-secondary" : "text-fg-disabled",
        )}
        size={16}
      />
      <span
        className={twMerge(
          "font-medium",
          page ? "text-text-secondary" : "text-text-disabled",
        )}
      >
        {PLATFORM_LABELS[platform]}
      </span>
      <span
        className={twMerge(
          "ml-auto truncate",
          page ? "text-text-tertiary" : "text-text-disabled",
        )}
      >
        {page ? `@${page.username ?? page.name}` : "Not connected"}
      </span>
    </div>
  );
}

function scheduledPostsLabel(count: number) {
  if (count === 0) return "No posts scheduled this week";

  return `${count} ${count === 1 ? "post" : "posts"} scheduled this week`;
}
